import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { ConsentType } from '__generated__/globalTypes';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import { useFeatureFlagsSettled } from 'flags/useFeatureFlagsSettled';
import { getOutstandingConsents, GivenConsents } from './consentRequirements';
import { usePrivacyConsentRequirements } from './usePrivacyConsentRequirements';

const NO_CONSENTS_REQUIRED: ConsentType[] = [];

/** Stable identity, so the memo below does not rerun on a fresh `{}`. */
export const NO_CONSENTS_GIVEN: GivenConsents = {};

type OutstandingConsentsOutput = {
  outstandingConsents: ConsentType[];
  loading: boolean;
  error?: ApolloError;
};

/** What this location requires and this user has not given. */
export const useOutstandingConsents = (
  givenConsents?: GivenConsents,
): OutstandingConsentsOutput => {
  const subDomain = getSubdomainFromUrl();

  const piplConsentEnabled = useFeatureFlag<boolean | undefined>(
    FEATURE_FLAGS.PIPL_CONSENT_FLOW,
  );

  /**
   * An unread flag is `undefined` rather than off, so it counts as loading -
   * bounded by whether LaunchDarkly has settled at all, so an outage fails open
   * instead of blocking sign-up.
   */
  const featureFlagsSettled = useFeatureFlagsSettled();
  const flagResolved = piplConsentEnabled !== undefined || featureFlagsSettled;

  const { consentRequirements, loading, error } =
    usePrivacyConsentRequirements(subDomain);

  const outstandingConsents = useMemo(() => {
    if (!givenConsents || !piplConsentEnabled || error) {
      return NO_CONSENTS_REQUIRED;
    }

    const outstanding = getOutstandingConsents(
      consentRequirements,
      givenConsents,
    );

    return outstanding.length === 0 ? NO_CONSENTS_REQUIRED : outstanding;
  }, [consentRequirements, givenConsents, piplConsentEnabled, error]);

  return { outstandingConsents, loading: loading || !flagResolved, error };
};
